import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type ActivityScanField,
  type DuplicateScanBehavior,
  generateActivityScanFieldId,
} from '@/lib/activity-scan-fields';

interface ActivityScanSettingsEditorProps {
  allowDuplicateScans: boolean;
  onAllowDuplicateScansChange: (value: boolean) => void;
  duplicateScanBehavior: DuplicateScanBehavior;
  onDuplicateScanBehaviorChange: (value: DuplicateScanBehavior) => void;
  scanEntryFields: ActivityScanField[];
  onScanEntryFieldsChange: (fields: ActivityScanField[]) => void;
}

export function ActivityScanSettingsEditor({
  allowDuplicateScans,
  onAllowDuplicateScansChange,
  duplicateScanBehavior,
  onDuplicateScanBehaviorChange,
  scanEntryFields,
  onScanEntryFieldsChange,
}: ActivityScanSettingsEditorProps) {
  const addField = () => {
    onScanEntryFieldsChange([
      ...scanEntryFields,
      {
        id: generateActivityScanFieldId(),
        label: 'New field',
        type: 'TEXT',
        required: false,
      },
    ]);
  };

  const updateField = (id: string, patch: Partial<ActivityScanField>) => {
    onScanEntryFieldsChange(
      scanEntryFields.map((field) => (field.id === id ? { ...field, ...patch } : field))
    );
  };

  const removeField = (id: string) => {
    onScanEntryFieldsChange(scanEntryFields.filter((field) => field.id !== id));
  };

  return (
    <div className="space-y-6 border-t border-border pt-4">
      <div className="space-y-3">
        <h4 className="text-sm font-bold tracking-tight">Duplicate scans</h4>
        <div className="flex items-center space-x-2">
          <Switch
            id="allow-duplicate-scans"
            checked={allowDuplicateScans}
            onCheckedChange={onAllowDuplicateScansChange}
          />
          <Label htmlFor="allow-duplicate-scans" className="flex flex-col gap-1">
            <span>Allow scanning the same attendee more than once</span>
            <span className="font-normal text-xs text-muted-foreground">
              When disabled, a second scan for the same ticket is rejected.
            </span>
          </Label>
        </div>
        {allowDuplicateScans && (
          <div className="space-y-2">
            <Label htmlFor="duplicate-behavior">When attendee is scanned again</Label>
            <Select
              value={duplicateScanBehavior === 'REJECT' ? 'CREATE_NEW' : duplicateScanBehavior}
              onValueChange={(value) =>
                onDuplicateScanBehaviorChange(value as DuplicateScanBehavior)
              }
            >
              <SelectTrigger id="duplicate-behavior">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CREATE_NEW">Create a new scan log entry</SelectItem>
                <SelectItem value="UPDATE_EXISTING">Update the existing scan log</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h4 className="text-sm font-bold tracking-tight">Entry fields per scan</h4>
            <p className="text-xs text-muted-foreground mt-1">
              Shown after each scan so staff can record t-shirt given, size, asset ID, and more.
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addField}>
            <Plus className="h-4 w-4 mr-1" />
            Add field
          </Button>
        </div>

        {scanEntryFields.length === 0 ? (
          <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border p-4 text-center">
            No entry fields. Scans are recorded immediately after QR check-in.
          </p>
        ) : (
          <div className="space-y-3">
            {scanEntryFields.map((field) => (
              <div
                key={field.id}
                className="rounded-xl border border-border bg-muted/20 p-4 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 flex-1">
                    <div className="space-y-2">
                      <Label>Label</Label>
                      <Input
                        value={field.label}
                        onChange={(e) => updateField(field.id, { label: e.target.value })}
                        placeholder="e.g. T-shirt given"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Type</Label>
                      <Select
                        value={field.type}
                        onValueChange={(value) =>
                          updateField(field.id, {
                            type: value as ActivityScanField['type'],
                          })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="TOGGLE">Toggle (yes / no)</SelectItem>
                          <SelectItem value="TEXT">Text (manual entry)</SelectItem>
                          <SelectItem value="BARCODE">Barcode / QR scan</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-destructive hover:text-destructive shrink-0"
                    onClick={() => removeField(field.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                {field.type !== 'TOGGLE' && (
                  <div className="space-y-2">
                    <Label>Placeholder</Label>
                    <Input
                      value={field.placeholder || ''}
                      onChange={(e) => updateField(field.id, { placeholder: e.target.value })}
                      placeholder={
                        field.type === 'BARCODE' ? 'Scan or type asset ID' : 'e.g. Medium'
                      }
                    />
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <Switch
                    id={`required-${field.id}`}
                    checked={field.required ?? false}
                    onCheckedChange={(checked) => updateField(field.id, { required: checked })}
                  />
                  <Label htmlFor={`required-${field.id}`}>Required before completing scan</Label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
